import { Component, EventEmitter, Input, OnInit, OnChanges, SimpleChanges, Output } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { RoleControllerService } from "../../../../../../services/role-controller/role-controller.service";
import { Store } from "../../../../../../services/store.service";
import { BaseError } from "../../../../base-error";

@Component({
    selector: "role-flow-registration",
    templateUrl: "./role-flow-registration.component.html",
    providers: [RoleControllerService],
})

export class RoleFlowRegistrationComponent extends BaseError implements OnInit, OnChanges {

    @Input() public roles: any[];
    @Input() public registrations: any[];
    @Input() public selectedRoleId: string = null;
    @Output() public modalClosedEvent = new EventEmitter();
    @Output() public registrationSelectionChanged = new EventEmitter();
    @Output() public showRegistrationsFilter = new EventEmitter();
    public rolesRegistrationsList: any[] = [];
    public all: boolean = true;
    private isInitializing: boolean = true;
    private selectedRoleRegistrationIds: Set<string> = new Set();

    constructor(private roleController: RoleControllerService,
                public store: Store,
                private route: ActivatedRoute, toastr: ToastrService) {
        super(toastr);
    }

    public ngOnInit(): void {
        this.isInitializing = true;
        this.populateRegistrations();
        this.restoreFromLocalStorage();
        this.isInitializing = false;
    }

    public ngOnChanges(changes: SimpleChanges): void {
        // Repopulate registrations when roles input changes, but don't emit during initialization
        if (changes.roles && !changes.roles.firstChange && !this.isInitializing) {
            const currentLength = this.rolesRegistrationsList.length;
            this.populateRegistrations();
            // Only restore and emit if the registration list actually changed
            if (this.rolesRegistrationsList.length !== currentLength) {
                this.restoreFromLocalStorage();
            }
        }

        // Update locked registrations when selectedRoleId changes
        if (changes.selectedRoleId) {
            this.updateLockedRegistrations();
        }
    }

    private restoreFromLocalStorage(): void {
        const selects = localStorage.getItem("roleRegistrationSelection");
        if (selects) {
            const elements = JSON.parse(selects);
            if (elements && elements.length) {
                elements.forEach((e) => {
                    this.rolesRegistrationsList.forEach((r) => {
                        if (e.id) {
                            if (r.id === e.id) {
                                r.checked = e.checked;
                            }
                        } else {
                            r.checked = true;
                        }
                    });
                });
                // Only emit if not initializing
                if (!this.isInitializing) {
                    setTimeout(() => this.checkSelection());
                }
            }
        } else {
            this.saveChanges();
        }
    }

    private updateLockedRegistrations(): void {
        this.selectedRoleRegistrationIds.clear();

        if (this.selectedRoleId && this.roles && Array.isArray(this.roles)) {
            // Find the selected role and get its registrations
            this.roles.forEach((roleGroup) => {
                if (Array.isArray(roleGroup)) {
                    roleGroup.forEach((role) => {
                        if (role.id === this.selectedRoleId && role.registrations) {
                            role.registrations.forEach((reg) => {
                                this.selectedRoleRegistrationIds.add(reg.registrationId);
                            });
                        }
                    });
                }
            });
        }

        // Update the locked property on registration items
        this.rolesRegistrationsList.forEach((reg) => {
            reg.locked = this.selectedRoleRegistrationIds.has(reg.id);
            // Ensure locked registrations are always checked
            if (reg.locked && !reg.checked) {
                reg.checked = true;
            }
        });

        this.getAlStatus();
    }

    public populateRegistrations(): void {
        const registrationMap = new Map();

        // Extract all registrations from roles
        if (this.roles && Array.isArray(this.roles)) {
            this.roles.forEach((roleGroup) => {
                if (Array.isArray(roleGroup)) {
                    roleGroup.forEach((role) => {
                        if (role.registrations && role.registrations.length > 0) {
                            role.registrations.forEach((reg) => {
                                if (!registrationMap.has(reg.registrationId)) {
                                    const isLocked = this.selectedRoleRegistrationIds.has(reg.registrationId);
                                    registrationMap.set(reg.registrationId, {
                                        id: reg.registrationId,
                                        name: reg.registrationName || reg.name,
                                        checked: true,
                                        locked: isLocked,
                                    });
                                }
                            });
                        }
                    });
                }
            });
        }

        // If no registrations found from roles, try using the registrations input directly
        if (registrationMap.size === 0 && this.registrations && this.registrations.length > 0) {
            this.registrations.forEach((reg) => {
                const isLocked = this.selectedRoleRegistrationIds.has(reg.id);
                registrationMap.set(reg.id, {
                    id: reg.id,
                    name: reg.name,
                    checked: true,
                    locked: isLocked,
                });
            });
        }

        this.rolesRegistrationsList = Array.from(registrationMap.values());
        this.updateLockedRegistrations();
        this.getAlStatus();
        this.showRegistrationsFilter.emit(this.rolesRegistrationsList.length > 0);
    }

    public getAlStatus() {
        let areAllChecked = true;
        this.rolesRegistrationsList?.forEach((r) => {
            if (r.checked === false) {
                areAllChecked = false;
            }
        });
        this.all = areAllChecked;
    }

    public registrationToggle(reg): void {
        // Don't allow toggling locked registrations
        if (reg.locked) {
            return;
        }

        reg.checked = !reg.checked;
        if (!reg.checked) {
            this.all = false;
        } else {
           this.getAlStatus();
        }
        this.checkSelection();
    }

    public selectAll(): void {
        this.rolesRegistrationsList.forEach((r) => {
            r.checked = true;
        });
        this.all = true;
        this.checkSelection();
    }

    public unselectAll(): void {
        this.rolesRegistrationsList.forEach((r) => {
            r.checked = false;
        });
        this.all = false;
        this.checkSelection();
    }

    public checkSelection(): void {
        this.registrationSelectionChanged.emit(this.rolesRegistrationsList);
        this.saveChanges();
    }

    public saveChanges(): void {
        localStorage.setItem("roleRegistrationSelection", JSON.stringify(this.rolesRegistrationsList));
    }

}
